import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";

const Privacy = () => {
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://namebiolab.lovable.app/privacy#webpage",
          "url": "https://namebiolab.lovable.app/privacy",
          "name": "Privacy Policy - NameBioLab",
          "description": "Learn how NameBioLab collects, uses, and protects your data. Our commitment to user privacy and data security.",
          "isPartOf": {
            "@id": "https://namebiolab.lovable.app/#webapp"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://namebiolab.lovable.app/privacy#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://namebiolab.lovable.app/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Privacy Policy",
              "item": "https://namebiolab.lovable.app/privacy"
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
            <p>
              Welcome to NameBioLab ("we," "our," or "us"). We respect your privacy and are committed to protecting 
              your personal data. This privacy policy explains how we collect, use, and safeguard your information 
              when you use our website at namebiolab.com and our AI-powered username and bio generation service.
            </p>
            <p className="mt-4">
              By using our website, you agree to the collection and use of information in accordance with this policy. 
              If you do not agree with our policies and practices, please do not use our service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Generator Input Data:</strong> Keywords, interests, personality traits, platform preferences, and other information you enter to generate usernames and bios. This data is processed in real-time and is not permanently stored on our servers.</li>
              <li><strong>Contact Information:</strong> Email address and name if you contact us through our contact form.</li>
              <li><strong>Correspondence:</strong> Any communications you send to us, including feedback, questions, and support requests.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Usage Data:</strong> Pages visited, time spent on pages, links clicked, and features used.</li>
              <li><strong>Device Information:</strong> Browser type and version, operating system, device type, screen resolution, and language preferences.</li>
              <li><strong>Log Data:</strong> IP address, access times, referring URLs, and crash reports.</li>
              <li><strong>Location Data:</strong> Approximate geographic location based on IP address.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">Cookies and Tracking Technologies</h3>
            <p>We use cookies and similar tracking technologies including:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for the website to function properly.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site (Google Analytics).</li>
              <li><strong>Advertising Cookies:</strong> Used by our advertising partners (Google AdSense, Ezoic) to display relevant ads based on your browsing behavior.</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service Delivery:</strong> To generate personalized usernames and bios based on your input and provide our core AI-powered services.</li>
              <li><strong>Service Improvement:</strong> To improve our AI algorithms, website functionality, and user experience based on usage patterns and feedback.</li>
              <li><strong>Analytics:</strong> To analyze website traffic, understand user behavior, and measure the effectiveness of our content and features.</li>
              <li><strong>Advertising:</strong> To display relevant, personalized advertisements through our advertising partners (Google AdSense, Ezoic) and measure ad performance.</li>
              <li><strong>Communication:</strong> To respond to your inquiries, send important service updates, and provide customer support.</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations, enforce our terms of service, and protect our rights and the rights of others.</li>
              <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security vulnerabilities.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your browsing experience, analyze site 
              traffic, and deliver personalized advertisements. Cookies are small text files stored on your device 
              that help us recognize you and remember your preferences.
            </p>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">Managing Cookies</h3>
            <p>You have several options to manage cookies:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Cookie Consent Banner:</strong> When you first visit our site, you can accept or decline non-essential cookies through our consent banner.</li>
              <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies. Visit your browser's help section for instructions.</li>
              <li><strong>Opt-Out Tools:</strong> You can opt out of personalized advertising by visiting the <a href="https://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">NAI opt-out page</a> or <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">DAA opt-out page</a>.</li>
            </ul>
            <p className="mt-3">
              <strong>Note:</strong> Disabling cookies may affect website functionality and your user experience. Some features may not work properly without cookies enabled.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Third-Party Services and Advertising</h2>
            
            <h3 className="text-xl font-semibold mb-3">Google AdSense</h3>
            <p>
              We use Google AdSense to display advertisements on our website. Google AdSense uses cookies and web beacons 
              to serve ads based on your prior visits to our website and other websites on the Internet. Google's use of 
              advertising cookies enables it and its partners to serve ads based on your visit to our site and/or other 
              sites on the Internet.
            </p>
            <p className="mt-3">
              You may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Ads Settings</a> or by visiting <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.aboutads.info</a>.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">Ezoic</h3>
            <p>
              We partner with Ezoic to optimize ad placements and improve user experience. Ezoic may use cookies 
              and similar technologies to collect information about your browsing behavior for analytics and 
              advertising purposes. For more information, visit <a href="https://www.ezoic.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Ezoic's Privacy Policy</a>.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">Google Analytics</h3>
            <p>
              We use Google Analytics to analyze website traffic and usage patterns. Google Analytics uses cookies 
              to collect information such as how often users visit the site, what pages they visit, and what other 
              sites they used prior to coming to our site. We use this information to improve our website and services.
            </p>
            <p className="mt-3">
              Google Analytics collects only the IP address assigned to you on the date you visit this site, not your 
              name or other identifying information. You can opt out of Google Analytics by installing the 
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline"> Google Analytics Opt-out Browser Add-on</a>.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">Important Notice</h3>
            <p>
              These third-party services have their own privacy policies. We do not control and are not responsible 
              for the privacy practices of these third parties. We encourage you to review their privacy policies:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Privacy Policy</a></li>
              <li><a href="https://www.ezoic.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Ezoic Privacy Policy</a></li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Data Security</h2>
            <p>
              We implement reasonable administrative, technical, and physical security measures designed to protect 
              your personal information from unauthorized access, disclosure, alteration, and destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Secure server infrastructure with encryption</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication procedures</li>
              <li>Secure data transmission protocols (HTTPS/SSL)</li>
            </ul>
            <p className="mt-3">
              However, please be aware that no method of transmission over the Internet or method of electronic 
              storage is 100% secure. While we strive to use commercially acceptable means to protect your personal 
              information, we cannot guarantee its absolute security. You use our service at your own risk.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined 
              in this privacy policy, unless a longer retention period is required or permitted by law. Generator 
              input data is processed in real-time and is not permanently stored. Analytics and usage data may be 
              retained for up to 26 months. Log data is typically retained for 90 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Children's Privacy</h2>
            <p>
              Our service is not intended for children under the age of 13 (or 16 in the European Economic Area). 
              We do not knowingly collect personal information from children under these ages. If you are a parent 
              or guardian and believe your child has provided us with personal information, please contact us 
              immediately. If we discover that we have collected personal information from a child under the 
              applicable age without verification of parental consent, we will take steps to delete that information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Your Privacy Rights</h2>
            <p>Depending on your location, you may have certain rights regarding your personal information:</p>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">GDPR Rights (European Users)</h3>
            <p>If you are located in the European Economic Area (EEA), you have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Right to Access:</strong> Request access to your personal data and receive a copy.</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete personal data.</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data under certain circumstances.</li>
              <li><strong>Right to Restriction:</strong> Request restriction of processing your personal data.</li>
              <li><strong>Right to Data Portability:</strong> Receive your personal data in a structured, commonly used format.</li>
              <li><strong>Right to Object:</strong> Object to processing of your personal data for direct marketing or legitimate interests.</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent.</li>
              <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">CCPA Rights (California Users)</h3>
            <p>If you are a California resident, you have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Right to Know:</strong> Request information about the categories and specific pieces of personal information we collect.</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information, subject to certain exceptions.</li>
              <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of your personal information (Note: We do not sell personal information).</li>
              <li><strong>Right to Non-Discrimination:</strong> You will not receive discriminatory treatment for exercising your privacy rights.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">Exercising Your Rights</h3>
            <p>
              To exercise any of these rights, please <Link to="/contact" className="text-primary hover:underline">contact us</Link>. 
              We will respond to your request within 30 days. We may need to verify your identity before processing your request.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have data protection laws that are different from the laws of your country. We take 
              appropriate safeguards to ensure that your personal information remains protected in accordance with 
              this privacy policy when transferred internationally.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Changes to This Privacy Policy</h2>
            <p>
              We may update this privacy policy from time to time to reflect changes in our practices, technology, 
              legal requirements, or other factors. We will notify you of any material changes by posting the new 
              privacy policy on this page and updating the "Last updated" date at the top of this policy.
            </p>
            <p className="mt-3">
              We encourage you to review this privacy policy periodically to stay informed about how we are protecting 
              your information. Your continued use of our service after any changes constitutes your acceptance of the 
              updated privacy policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this privacy policy or our data practices, 
              please contact us:
            </p>
            <ul className="list-none pl-0 space-y-2 mt-3">
              <li><strong>Website:</strong> <Link to="/contact" className="text-primary hover:underline">Contact Form</Link></li>
              <li><strong>Email:</strong> privacy@namebiolab.com</li>
            </ul>
            <p className="mt-3">
              We will respond to all legitimate requests within 30 days. For data protection inquiries from EEA residents, 
              we will respond within the timeframes required by GDPR.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default Privacy;
